import React, { useCallback, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { bulkAddExternalSiteMapping } from 'store/sitesManager'
import { ExternalSiteMappingType, PatchResponse, RootState } from 'types'
import { SitesManagerState } from 'store/sitesManager/reducer'
import { ExternalSiteMapping } from './component'
import { AppDispatch } from 'store/types'

export const ExternalSiteMappingContainer = () => {
  const dispatch = useDispatch<AppDispatch>()

  const {
    externalSiteMapping: { uploadResults, error },
    isLoading,
  } = useSelector<RootState, SitesManagerState>(state => state.sitesManager)

  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState<string>('')

  const formattedUploadResults = useMemo<Array<{
    siteId?: number | string
    siteName?: string
    externalSiteId?: number | string
    district?: string
    error?: string[]
  }> | null>(() => {
    if (!uploadResults) {
      return null
    }

    const { createdItems = [], failedItems = [] } =
      uploadResults as PatchResponse<ExternalSiteMappingType>

    const formatted: Array<{
      siteId?: number | string
      externalSiteId?: number | string
      district?: string
      error?: string[]
    }> = createdItems.map(externalSiteMapping => ({
      district: externalSiteMapping.district,
      externalSiteId: externalSiteMapping.externalSiteId,
      siteId: externalSiteMapping.siteId,
    }))
    // failedItems is an array of PatchFailure<ExternalSiteMapping> objects
    // Each PatchFailure has an item (ExternalSiteMapping) and errors (string[])
    failedItems.forEach(failure => {
      const { item, errors } = failure
      // Create a separate row for each error message
      errors.forEach(errorMessage => {
        formatted.unshift({
          district: item?.district,
          externalSiteId: item?.externalSiteId,
          siteId: item?.siteId,
          error: [errorMessage],
        })
      })
    })
    return formatted.length > 0 ? formatted : null
  }, [uploadResults])

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0]
      if (selectedFile) {
        setFile(selectedFile)
        setFileName(selectedFile.name)
      }
    },
    [],
  )

  const handleUpload = useCallback(
    (file: File) => {
      if (file) {
        dispatch(bulkAddExternalSiteMapping(file))
        // Clear file and fileName immediately after submission
        setFile(null)
        setFileName('')
      }
    },
    [dispatch],
  )

  return (
    <ExternalSiteMapping
      error={error}
      file={file}
      fileName={fileName}
      handleUpload={handleUpload}
      isLoading={isLoading}
      onFileChange={handleFileChange}
      formattedUploadResults={formattedUploadResults}
    />
  )
}
